import { useAuth } from 'src/auth/AuthContext';
import React, { useMemo, useState } from 'react';
import {
  Menu,
  MenuItem,
  Avatar,
  CssBaseline,
  Box,
  Typography,
  ButtonBase
} from '@mui/material';
import {
  PersonOutline,
  StorefrontOutlined,
  AdminPanelSettingsOutlined,
  SubscriptionsOutlined,
  FileDownloadOutlined,
  InsightsOutlined,
  SupportAgent,
  ReceiptOutlined
} from '@mui/icons-material';
import { Link, Outlet, matchPath, useLocation } from 'react-router-dom';
import { useLocations } from 'src/queries/locations';
import { useActiveLocation } from 'src/queries/activeLocation';
import { PermissionRole } from '../constants';
import { doSignOut } from '../utils/auth';
import anyperksLogo from '../static/anyperks.png';
import SupportDialog from './dialogs/SupportDialog';
import { getMarketingMaterialDownload } from '../utils/marketingMaterials';

const sidebarWidth = 248;
const bodyFont = "'Instrument Sans', system-ui, sans-serif";
const brewprksLogoUrl = 'https://assets.brewprks.com/media/images/logo-dark.svg';

interface SidebarItem {
  text: string;
  icon?: React.ReactElement;
  path?: string;
  isHeader?: boolean;
}

const navItemSx = (active: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '12px',
  height: 42,
  px: '12px',
  borderRadius: '10px',
  width: '100%',
  fontFamily: bodyFont,
  fontSize: 15,
  fontWeight: active ? 600 : 500,
  color: active ? '#0E4F70' : '#46525C',
  backgroundColor: active ? '#EAF6FC' : 'transparent',
  textDecoration: 'none',
  '&:hover': { backgroundColor: active ? '#EAF6FC' : '#EEF2F5' },
  '& svg': { fontSize: 20 }
});

const MainLayout: React.FC = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const { pathname } = useLocation();

  const user = useAuth();
  const userId = useAuth().id;
  const { data: locations = [] } = useLocations(userId);
  // The sidebar renders outside the /locations/:id route subtree, so it derives
  // the active location from the URL and has to tolerate it not being loaded yet.
  const { data: merchant } = useActiveLocation();

  // The active location comes from the URL, not from in-memory selection —
  // the sidebar's location section only exists inside /locations/:id/*.
  const locationMatch =
    matchPath('/locations/:id/*', pathname) ?? matchPath('/locations/:id', pathname);
  const activeLocationId = locationMatch ? Number(locationMatch.params.id) : undefined;
  const activeLocation = locations.find((item) => item.id === activeLocationId);
  const inLocationContext =
    activeLocationId !== undefined &&
    Number.isInteger(activeLocationId) &&
    (user.isSuperAdmin || activeLocation?.userPermissionRole === PermissionRole.Admin);
  const selectedMerchantTypeId = activeLocation?.typeId;
  const isBrewprksContext =
    selectedMerchantTypeId === 1 || selectedMerchantTypeId === undefined;

  const handleUserCardClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      await doSignOut();
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  const handleDownloadMarketingMaterial = async () => {
    if (!merchant?.type?.name) return;

    try {
      // Presigned URL with Content-Disposition: attachment — navigating to
      // it triggers the save dialog with the filename set server-side.
      const { url } = await getMarketingMaterialDownload(merchant.type.name);
      window.location.assign(url);
    } catch (error) {
      console.error('Failed to fetch marketing material download URL:', error);
    }
  };

  // Account-level items only — location-scoped navigation lives as tabs in
  // the /locations/:id detail view (LocationRoute), not in the sidebar.
  const sidebarItems = useMemo(() => {
    const items: SidebarItem[] = [
      { text: 'Locations', icon: <StorefrontOutlined />, path: '/locations' },
      { text: 'Account', icon: <PersonOutline />, path: '/user' },
    ];

    if (user.isSuperAdmin) {
      items.push(
        { text: 'Admin', isHeader: true },
        { text: 'Flyer Admin', icon: <AdminPanelSettingsOutlined />, path: '/admin/flyers' },
        { text: 'Subscription Admin', icon: <SubscriptionsOutlined />, path: '/admin/subscriptions' },
        { text: 'Stats Admin', icon: <InsightsOutlined />, path: '/admin/stats' },
        { text: 'Uninvoiced Payments', icon: <ReceiptOutlined />, path: '/admin/uninvoiced-payments' }
      );
    }

    return items;
  }, [user.isSuperAdmin]);

  const displayName =
    user.displayName || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email;
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  const isItemActive = (path?: string) => {
    if (!path) return false;
    if (path === '/locations') return pathname === path || pathname.startsWith('/locations/');
    return pathname === path;
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', backgroundColor: '#F4F7F9' }}>
      <CssBaseline />

      <Box
        component='aside'
        sx={{
          width: sidebarWidth,
          flex: 'none',
          borderRight: '1px solid #E6ECF0',
          backgroundColor: '#F4F7F9',
          display: 'flex',
          flexDirection: 'column',
          p: '28px 16px 20px',
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflowY: 'auto'
        }}
      >
        {isBrewprksContext ? (
          <Box
            component='img'
            src={brewprksLogoUrl}
            alt='Brewprks'
            sx={{ width: 132, m: '0 12px 36px', alignSelf: 'flex-start' }}
          />
        ) : (
          <Box
            component='img'
            src={anyperksLogo}
            alt='AnyPerks'
            sx={{ width: 132, m: '0 12px 36px', alignSelf: 'flex-start' }}
          />
        )}

        <Box component='nav' sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {sidebarItems.map((item) =>
            item.isHeader ? (
              <Typography
                key={item.text}
                sx={{
                  fontFamily: bodyFont,
                  fontWeight: 600,
                  fontSize: 11,
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                  color: '#7C8893',
                  px: '12px',
                  pt: '20px',
                  pb: '4px'
                }}
              >
                {item.text}
              </Typography>
            ) : (
              <ButtonBase
                key={item.text}
                component={Link}
                to={item.path ?? ''}
                sx={navItemSx(isItemActive(item.path))}
              >
                {item.icon}
                {item.text}
              </ButtonBase>
            )
          )}
        </Box>

        <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {inLocationContext && merchant?.type?.name && (
            <ButtonBase
              onClick={handleDownloadMarketingMaterial}
              sx={navItemSx(false)}
            >
              <FileDownloadOutlined />
              Marketing Materials
            </ButtonBase>
          )}

          <ButtonBase
            onClick={handleUserCardClick}
            aria-label='Account menu'
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '10px',
              p: '10px 12px',
              borderRadius: '12px',
              border: '1px solid #E6ECF0',
              backgroundColor: '#fff',
              textAlign: 'left',
              width: '100%',
              '&:hover': { backgroundColor: '#F9FBFC' }
            }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                backgroundColor: '#17222C',
                color: '#fff',
                fontFamily: bodyFont,
                fontWeight: 600,
                fontSize: 13
              }}
            >
              {initials || undefined}
            </Avatar>
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ fontFamily: bodyFont, fontWeight: 600, fontSize: 14, color: '#17222C' }}
              >
                {displayName}
              </Typography>
              <Typography
                noWrap
                sx={{ fontFamily: bodyFont, fontSize: 12, color: '#5F6B76' }}
              >
                {user.email}
              </Typography>
            </Box>
          </ButtonBase>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box
        component='main'
        sx={{
          flex: 1,
          minWidth: 0,
          p: { xs: '24px 20px 80px', md: '40px 48px 80px' },
          overflowX: 'hidden'
        }}
      >
        <Outlet />
      </Box>

      <ButtonBase
        aria-label='contact support'
        onClick={() => setSupportDialogOpen(true)}
        sx={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          width: 52,
          height: 52,
          borderRadius: '50%',
          backgroundColor: '#17222C',
          color: '#fff',
          boxShadow: '0 8px 20px -8px rgba(23,34,44,.5)',
          '&:hover': { backgroundColor: '#223140' },
          zIndex: 1200
        }}
      >
        <SupportAgent sx={{ fontSize: 24 }} />
      </ButtonBase>
      <SupportDialog
        open={supportDialogOpen}
        onClose={() => setSupportDialogOpen(false)}
      />
    </Box>
  );
};

export default MainLayout;
